import { Link, Outlet } from '@tanstack/react-router';
import { GithubIcon, LinkedinIcon, MenuIcon, MoonIcon, SunIcon, TwitterIcon, XIcon } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { beginLogin, isHiveMode } from '@/lib/hive';

const THEME_KEY = 'athena-theme';

function getInitialTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  const stored = window.localStorage.getItem(THEME_KEY);
  if (stored === 'dark' || stored === 'light') return stored;
  return 'light';
}

const NAV_LINKS: Array<{ href: string; label: string }> = [
  { href: '/#problem', label: 'Problem' },
  { href: '/#dimensions', label: 'Methodology' },
  { href: '/#how-it-works', label: 'How it works' },
  { href: '/sample-report', label: 'Sample report' },
  { href: '/#roi', label: 'Calculate ROI' },
  { href: '/#pricing', label: 'Pricing' },
  { href: '/#faq', label: 'FAQ' },
];

export function MarketingShell() {
  const [theme, setTheme] = useState<'light' | 'dark'>(getInitialTheme);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');
    window.localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  return (
    <div className="min-h-full">
      <header className="sticky top-0 z-50 border-b border-slate-200/70 bg-white/80 backdrop-blur dark:border-slate-800 dark:bg-slate-950/80">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
          <Link to="/" className="flex flex-col leading-none">
            <span className="font-mono text-lg font-bold tracking-tight">athena</span>
            <span className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
              by Momentum AI
            </span>
          </Link>

          <nav className="hidden items-center gap-5 text-sm text-slate-600 lg:flex dark:text-slate-300">
            {NAV_LINKS.map((link) => (
              <NavAnchor key={link.href} href={link.href}>
                {link.label}
              </NavAnchor>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              aria-label="Toggle dark mode"
              className="rounded-md p-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              {theme === 'dark' ? <SunIcon className="h-4 w-4" /> : <MoonIcon className="h-4 w-4" />}
            </button>
            <AuthCta to="/login" variant="ghost" className="hidden sm:inline-flex">
              Log in
            </AuthCta>
            <AuthCta to="/signup" className="hidden sm:inline-flex">
              Get a free audit
            </AuthCta>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Toggle menu"
              aria-expanded={menuOpen}
              className="rounded-md p-2 text-slate-600 hover:bg-slate-100 lg:hidden dark:text-slate-300 dark:hover:bg-slate-800"
            >
              {menuOpen ? <XIcon className="h-5 w-5" /> : <MenuIcon className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="border-t border-slate-200 bg-white lg:hidden dark:border-slate-800 dark:bg-slate-950">
            <div className="mx-auto flex max-w-7xl flex-col gap-1 px-6 py-4 text-sm">
              {NAV_LINKS.map((link) => (
                <NavAnchor
                  key={link.href}
                  href={link.href}
                  onClick={() => setMenuOpen(false)}
                  className="block py-2 text-slate-700 dark:text-slate-200"
                >
                  {link.label}
                </NavAnchor>
              ))}
              <div className="mt-3 flex flex-col gap-2 border-t border-slate-200 pt-3 dark:border-slate-800">
                <AuthCta to="/login" variant="outline" onNavigate={() => setMenuOpen(false)}>
                  Log in
                </AuthCta>
                <AuthCta to="/signup" onNavigate={() => setMenuOpen(false)}>
                  Get a free audit
                </AuthCta>
              </div>
            </div>
          </div>
        )}
      </header>

      <main>
        <Outlet />
      </main>

      <footer className="border-t border-slate-200 dark:border-slate-800">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 py-12 md:grid-cols-4">
          <div className="md:col-span-1">
            <div className="mb-4 flex flex-col leading-none">
              <span className="font-mono text-lg font-bold">athena</span>
              <span className="mt-0.5 text-xs text-slate-500">a product by Momentum AI</span>
            </div>
            <div className="flex items-center gap-2">
              <FooterSocial label="Twitter" href="#">
                <TwitterIcon className="h-4 w-4" />
              </FooterSocial>
              <FooterSocial label="GitHub" href="#">
                <GithubIcon className="h-4 w-4" />
              </FooterSocial>
              <FooterSocial label="LinkedIn" href="#">
                <LinkedinIcon className="h-4 w-4" />
              </FooterSocial>
            </div>
          </div>

          <FooterColumn title="Product">
            <FooterLink to="/pricing">Pricing</FooterLink>
            <FooterLink to="/sample-report">Sample Report</FooterLink>
            <FooterLink to="/methodology">Methodology</FooterLink>
          </FooterColumn>

          <FooterColumn title="Resources">
            <FooterAnchor href="/#roi">ROI calculator</FooterAnchor>
            <FooterAnchor href="/#faq">FAQ</FooterAnchor>
          </FooterColumn>

          <FooterColumn title="Company">
            <FooterAnchor href="https://momentumai.dev">About Momentum AI</FooterAnchor>
            {isHiveMode() ? (
              <li>
                <button
                  type="button"
                  onClick={() => void beginLogin('/app')}
                  className="hover:text-slate-900 dark:hover:text-white"
                >
                  Contact
                </button>
              </li>
            ) : (
              <FooterLink to="/signup">Contact</FooterLink>
            )}
            <FooterAnchor href="#">Privacy</FooterAnchor>
          </FooterColumn>
        </div>

        <div className="border-t border-slate-200 dark:border-slate-800">
          <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-6 text-xs text-slate-500">
            <span>© {new Date().getFullYear()} Momentum AI</span>
            <a href="#" className="hover:text-slate-900 dark:hover:text-white">
              athena.momentumai.dev
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

/**
 * Log-in / sign-up CTA. Supabase mode links to the local auth pages; Hive
 * mode starts Hive single sign-on instead (the local pages stay registered
 * for stale bookmarks and self-redirect).
 */
function AuthCta({
  to,
  variant,
  className,
  onNavigate,
  children,
}: {
  to: '/login' | '/signup';
  variant?: 'ghost' | 'outline';
  className?: string;
  onNavigate?: () => void;
  children: React.ReactNode;
}) {
  if (isHiveMode()) {
    return (
      <Button
        variant={variant}
        className={className}
        onClick={() => {
          onNavigate?.();
          void beginLogin('/app');
        }}
      >
        {children}
      </Button>
    );
  }
  return (
    <Button variant={variant} className={className} asChild>
      <Link to={to} onClick={onNavigate}>
        {children}
      </Link>
    </Button>
  );
}

function NavAnchor({
  href,
  children,
  className,
  onClick,
}: {
  href: string;
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}) {
  const baseClasses =
    className ??
    'hover:text-slate-900 dark:hover:text-white';

  if (href.startsWith('/#')) {
    const hash = href.slice(2);
    return (
      <Link
        to="/"
        hash={hash}
        className={baseClasses}
        onClick={onClick}
      >
        {children}
      </Link>
    );
  }

  return (
    <Link to={href} className={baseClasses} onClick={onClick}>
      {children}
    </Link>
  );
}

function FooterColumn({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-4 font-mono text-xs uppercase tracking-wider text-slate-500">{title}</div>
      <ul className="space-y-2.5 text-sm text-slate-600 dark:text-slate-400">{children}</ul>
    </div>
  );
}

function FooterLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <li>
      <Link to={to} className="hover:text-slate-900 dark:hover:text-white">
        {children}
      </Link>
    </li>
  );
}

function FooterAnchor({ href, children }: { href: string; children: React.ReactNode }) {
  if (href.startsWith('/#')) {
    return (
      <li>
        <Link to="/" hash={href.slice(2)} className="hover:text-slate-900 dark:hover:text-white">
          {children}
        </Link>
      </li>
    );
  }
  const external = href.startsWith('http');
  return (
    <li>
      <a
        href={href}
        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : null)}
        className="hover:text-slate-900 dark:hover:text-white"
      >
        {children}
      </a>
    </li>
  );
}

function FooterSocial({
  label,
  href,
  children,
}: {
  label: string;
  href: string;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      aria-label={label}
      className="flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition-colors hover:border-slate-400 hover:text-slate-900 dark:border-slate-800 dark:hover:border-slate-600 dark:hover:text-white"
    >
      {children}
    </a>
  );
}
